import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Box, IconButton, Tooltip } from '@mui/material';

import { onboardColors } from '../../theme/onboardTokens';
import { font } from '../styles';
import { usePortalCompact } from '../usePortalCompact';

// FileDropzone skips a file that is already picked, so this key is unique.
export const fileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

export const formatFileSize = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  if (mb >= 0.1) return `${mb.toFixed(1)}mb`;
  return `${Math.max(1, Math.round(bytes / 1024))}kb`;
};

// Hover (or keyboard focus) blurs the thumbnail and reveals the size and remove button.
const REVEAL = '&:hover, &:focus-within';

const styles = {
  tile: {
    position: 'relative',
    width: 96,
    height: 96,
    flexShrink: 0,
    overflow: 'hidden',
    borderRadius: '8px',
    background: onboardColors.surface.white,
    [REVEAL]: {
      '& .portal-file-thumb': { filter: 'blur(4px)' },
      '& .portal-file-reveal': { opacity: 1 }
    }
  },
  name: {
    position: 'absolute',
    top: 12,
    left: 8,
    right: 8,
    fontFamily: font,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '20px',
    color: onboardColors.neutral.textStrong,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  thumb: {
    position: 'absolute',
    top: 38,
    left: 6,
    right: 6,
    height: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: onboardColors.neutral.icon
  },
  size: {
    position: 'absolute',
    top: 64,
    left: 0,
    right: 0,
    fontFamily: font,
    fontSize: 14,
    lineHeight: '20px',
    color: onboardColors.neutral.textStrong,
    textAlign: 'center',
    pointerEvents: 'none'
  },
  remove: {
    position: 'absolute',
    top: 30,
    left: 32,
    width: 32,
    height: 32,
    padding: 0,
    color: onboardColors.neutral.textStrong,
    '&:hover': { backgroundColor: 'transparent' }
  }
} as const;

const FileThumbnail: React.FC<{ file: File }> = ({ file }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // A data: URL rather than URL.createObjectURL: the portal's CSP (img-src 'self' data:)
  // blocks blob: images.
  useEffect(() => {
    setPreviewUrl(null);
    if (!file.type.startsWith('image/')) return undefined;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
    return () => reader.abort();
  }, [file]);

  return (
    <Box className="portal-file-thumb" sx={styles.thumb}>
      {previewUrl ? (
        <img
          alt=""
          onError={() => setPreviewUrl(null)}
          src={previewUrl}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 2 }}
        />
      ) : (
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 36 }} />
      )}
    </Box>
  );
};

interface Props {
  files: File[];
  onRemove: (index: number) => void;
}

/** Tiles shown inside the drop zone for files picked but not yet submitted. */
const SelectedFiles: React.FC<Props> = ({ files, onRemove }) => {
  // Touch screens have no hover, so the size and remove button stay visible there.
  const compact = usePortalCompact();

  if (files.length === 0) return null;

  return (
    <ul
      aria-label="Selected files"
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 24
      }}
    >
      {files.map((file, index) => (
        <Tooltip
          key={fileKey(file)}
          placement="top"
          slotProps={{ tooltip: { sx: { fontFamily: font, fontSize: 14 } } }}
          title={file.name}
        >
          <Box component="li" sx={[styles.tile, compact ? styles.tile[REVEAL] : {}]}>
            <span style={styles.name}>{file.name}</span>
            <FileThumbnail file={file} />
            <Box
              className="portal-file-reveal"
              sx={{ opacity: 0, transition: 'opacity 120ms ease' }}
            >
              <span style={styles.size}>{formatFileSize(file.size)}</span>
              <IconButton
                aria-label={`Remove ${file.name}`}
                id={`portal-remove-file-${index}`}
                onClick={() => onRemove(index)}
                sx={styles.remove}
              >
                <CloseIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </Box>
          </Box>
        </Tooltip>
      ))}
    </ul>
  );
};

export default SelectedFiles;
